import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { showAlertError } from 'src/components/alertError'
import { ModalLoader } from 'src/components/modalLoader'
import {
  ModalContainer,
  ModalContent,
  ModalRoot,
} from 'src/components/ui/modal'
import { useAuth } from 'src/hooks/auth'
import {
  pixKeyStepSchema,
  type PixKeyStepSchema,
} from 'src/interface/newPixSchema'
import { api } from 'src/services/api'
import { isAxiosError } from 'src/utils/isAxiosError'
import { setAuthToken } from 'src/utils/setAuthToken'
import type { BeneficiaryDataProps, NewPixFormProps } from '.'
import PixKeyImage from '../../../../../assets/PixKeyForm.svg'

interface PixKeyStepProps extends NewPixFormProps {
  setBeneficiaryData: React.Dispatch<React.SetStateAction<BeneficiaryDataProps>>
  setStep: React.Dispatch<React.SetStateAction<number>>
}
const keyTypes = ['EMAIL', 'CPF', 'CNPJ', 'TELEFONE', 'CHAVE_ALEATORIA']
export function PixKeyStep({
  nextStep,
  setBeneficiaryData,
  setStep,
}: PixKeyStepProps) {
  const { user, account } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PixKeyStepSchema>({
    resolver: zodResolver(pixKeyStepSchema),
  })
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [selectedKeyType, setSelectedKeyType] = useState<string>('EMAIL')

  async function onSubmit(data: PixKeyStepSchema) {
    setIsSubmiting(true)
    try {
      setAuthToken()

      if (user.role === 'ADMIN_BAG') {
        const response = await api.post<{
          end2end_id: string
          key: string
          key_type: string
          name: string
          cpf_cnpj: string
          bank_ispb: string
        }>('/pix/keys/check/admin', {
          key: data.key,
          keyType: selectedKeyType,
        })
        setBeneficiaryData({
          name: response?.data?.name,
          key: response?.data?.key,
          account: response?.data?.bank_ispb,
          keyType: selectedKeyType,
          endToEndId: response?.data.end2end_id,
          beneficiaryDocument: response?.data?.cpf_cnpj,
        })
      } else {
        const response = await api.post<{
          end2end_id: string
          key: string
          key_type: string
          name: string
          cpf_cnpj: string
          bank_ispb: string
        }>('/pix/keys/check', {
          key: data.key,
          accountId: account.virtual_account_id,
          keyType: selectedKeyType,
        })
        setBeneficiaryData({
          name: response?.data?.name,
          key: response?.data?.key,
          account: response?.data?.bank_ispb,
          keyType: selectedKeyType,
          endToEndId: response?.data.end2end_id,
          beneficiaryDocument: response?.data?.cpf_cnpj,
        })
      }

      nextStep()
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        console.log(error.response)
        if (error.response.status === 401) {
          return setStep(6)
        }
        const errorMessage = (error.response.data as { message: string })
          .message
        showAlertError(errorMessage)
      } else {
        showAlertError('Ocorreu um erro inesperado.')
      }
    } finally {
      setIsSubmiting(false)
    }
  }

  if (isSubmiting) {
    return <ModalLoader />
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-[1.875rem] items-center justify-center px-3 py-6  drop-shadow-md rounded"
    >
      <img src={PixKeyImage} alt="" className="w-52 h-52" />
      <span className="text-[1.875rem] font-bold ">Realizar novo pix</span>
      <div className="flex flex-col gap-2 items-start justify-start">
        <label htmlFor="key" className="font-semibold">
          Digite a chave
        </label>
        <input
          id="key"
          {...register('key')}
          type="text"
          required
          placeholder="Celular, E-mail, CPF, CNPJ ou Chave Aleatória"
          className="w-[467px] placeholder:text-lg placeholder:text-[#D3D1D1] border border-[#A0A0A0] rounded-[10px] py-3 px-5 placeholder:leading-tight"
        />
        <select
          onChange={(e) => {
            setSelectedKeyType(e.target.value)
          }}
          placeholder="selecione"
          className="w-[467px] placeholder:text-lg placeholder:text-[#D3D1D1] border border-[#A0A0A0] rounded-[10px] py-3 px-5 "
        >
          {keyTypes.map((keyType) => (
            <option value={keyType} key={keyType}>
              {keyType.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      {errors.key && <span className="text-red-500">{errors.key.message}</span>}
      <button
        type="submit"
        className="text-[0.9375rem] font-bold bg-[#004A8A] w-[475px] text-white h-[55px] flex items-center justify-center rounded-[10px] hover:brightness-75"
      >
        Confirmar e continuar
      </button>
      <ModalRoot open={isModalOpen} setIsOpen={setIsModalOpen}>
        <ModalContainer>
          <ModalContent></ModalContent>
        </ModalContainer>
      </ModalRoot>
    </form>
  )
}
